import type { ProgressSummaryWithoutEvaluated } from '@/components/templates/ADashboard/types'
import type { QuestionGroupByCompaniesProgressSummary } from '@/types/domain/QuestionGroup/ProgressSummary'
import type { EvaluationForBuyer } from '@/types/domain/Evaluation/List/Buyer'
import type { EvaluationListResponse } from '@/types/domain/Evaluation/List/Supplier'
import type { CompletionStatus } from '@/constants/common'

/**
 * TODO: 本当は @types/global.gtag.d.ts ファイルとして定義したいが、 Nuxt ではどうしていいかわからなかったので、ここに定義: start ↓
 */
declare global {
  interface Window {
    gtag: $GTag
  }
}
// 参考: https://github.com/GoogleChromeLabs/third-party-capital/blob/main/src/types/type-declarations.ts#L82-L91
// ↓ overload 形式では扱いづらいので union 形式に変更
declare type $GTag = (...args: (
  | [fn: 'js', opt: Date]
  | [fn: 'config', opt: string]
  | [fn: 'event', opt: string, opt2?: { [key: string]: any }]
  | [fn: 'set', opt: { [key: string]: string }]
  | [fn: 'get', opt: string]
  | [fn: 'consent', opt: 'default', opt2: { [key: string]: string }]
  | [fn: 'consent', opt: 'update', opt2: { [key: string]: string }]
  | [fn: 'config', opt: 'reset']
)) => void

/**
 * TODO: 本当は @types/global.gtag.d.ts ファイルとして定義したいが、 Nuxt ではどうしていいかわからなかったので、ここに定義: end ↑
 */
/**
 * asuene gtag ラッパー
 */
const _gtag: $GTag = (...args) => {
  if (window.gtag) {
    window.gtag(...args)
  }
  else {
    // eslint-disable-next-line no-console
    console.log('[no gtag]', ...args)
  }
}

// ga側の自動収集以外のイベントのトラッキングを有効にするかどうか
const ga4TrackingConfig = {
  enabledSourceTriggerPageView: true, // MEMO: GA側のデフォルトイベント名: `page_view`     これを true にするのであれば、 GA側の「ページビュー」の自動収集は OFF にできないので、カスタムイベントとして別のイベントを利用しないと二重計上が発生するので注意。
  enabledSourceTriggerClick: true, // MEMO: GA側のデフォルトイベント名: `click`         これを true にするのであれば、 GA側の「離脱クリック」の自動収集も OFF にしておくほうがややこしくはない気はする。
  enabledSourceTriggerFileDownload: true, // MEMO: GA側のデフォルトイベント名: `file_download` これを true にするのであれば、 GA側の「ファイルのダウンロード」の自動収集も OFF にしておくほうがややこしくはない気はする。
}

const _trackEvent = (
  eventName: 'custom_page_view' | 'click' | 'file_download',
  eventParams?: { [key: string]: any },
) => {
  try {
    // For GA4
    if (
      (eventName === 'custom_page_view' && ga4TrackingConfig.enabledSourceTriggerPageView)
      || (eventName === 'click' && ga4TrackingConfig.enabledSourceTriggerClick)
      || (eventName === 'file_download' && ga4TrackingConfig.enabledSourceTriggerFileDownload)
    ) {
      _gtag('event', eventName, {
        event_category: 'ESG_アプリ',
        non_interaction: true, // GA4 では無意味な気はするが、一応設定しておく。
        ...eventParams,
      })
    }
    else {
      // console.log(`[_trackEvent] ${eventName} is disabled`)
    }
    // For others...
  }
  catch (e) {
    // 何かしら予期せぬエラーが発生しても console 出力だけして後続処理は継続。
    // eslint-disable-next-line no-console
    console.error(e)
  }
}

/**
 https://docs.google.com/spreadsheets/d/1wMgswCjmWnzjW7WGcsvML271MmSPFNxyHGcSjig5ni0/edit?gid=1687771316#gid=1687771316
 (以下は copilot 用に上記をコピーして貼り付けたもの。（スプシに変更が会った時の差分確認にも使えるかも）)

1 イベント メディア 例：トップ_ロゴ https://earthene.com/media
2 event esg_app ログイン https://ecr.earthene.com/mypage/login - page_view ESG_アプリ 認証 ログイン
3 event esg_app パスワードリセット_メールアドレス入力 https://ecr.earthene.com/mypage/resetpassword - page_view ESG_アプリ 認証 パスワードリセット_メールアドレス入力
4 event esg_app パスワードリセット_パスワード入力 https://ecr.earthene.com/mypage/resetpasswordconfirm - page_view ESG_アプリ 認証 パスワードリセット_パスワード入力
5 event esg_app パスワードリセット_完了 https://ecr.earthene.com/mypage/resetpasswordcomplete - page_view ESG_アプリ 認証 パスワードリセット_完了
6 event esg_app SMS認証_認証コード入力 https://ecr.earthene.com/mypage/login-mfa - page_view ESG_アプリ 認証 SMS認証_認証コード入力
7 event esg_app ダッシュボード_サプライヤー https://ecr.earthene.com/mypage/dashboard - page_view ESG_アプリ ダッシュボード ダッシュボード_サプライヤー
8 event esg_app ダッシュボード_バイヤー https://ecr.earthene.com/mypage/evaluation/dashboard - page_view ESG_アプリ ダッシュボード ダッシュボード_バイヤー
9 event esg_app 調査票/サーベイ https://ecr.earthene.com/mypage/upload/common - page_view ESG_アプリ 調査票/サーベイ 調査票/サーベイ
10 event esg_app スコアカード_一覧 https://ecr.earthene.com/mypage/scorecard - page_view ESG_アプリ スコアカード スコアカード_一覧
11 event esg_app スコアカード_詳細 https://ecr.earthene.com/mypage/scorecard/detail - page_view ESG_アプリ スコアカード スコアカード_詳細
12 event esg_app 分析 https://ecr.earthene.com/mypage/evaluation/analysis - page_view ESG_アプリ 分析 分析
13 event esg_app 改善計画_企業詳細（サプライヤー） https://ecr.earthene.com/mypage/improvementplans - page_view ESG_アプリ 改善計画 改善計画_企業詳細（サプライヤー）
14 event esg_app 改善計画_企業一覧（バイヤー） https://ecr.earthene.com/mypage/evaluation/improvementplans - page_view ESG_アプリ 改善計画 改善計画_企業一覧（バイヤー）
16 event esg_app 設定_アカウント情報 https://ecr.earthene.com/mypage/settings/account - page_view ESG_アプリ 設定 設定_アカウント情報
17 event esg_app 設定_ユーザー追加 https://ecr.earthene.com/mypage/settings/account/create - page_view ESG_アプリ 設定 設定_ユーザー追加
18 event esg_app 設定_ユーザー編集 https://ecr.earthene.com/mypage/settings/account/XXX/edit - page_view ESG_アプリ 設定 設定_ユーザー編集
19 event esg_app 言語切り替えボタン - - click ESG_アプリ 共通 言語切り替えボタン
20 event esg_app ログアウトボタン - - click ESG_アプリ 認証 ログアウトボタン
21 event esg_app ログインボタン https://ecr.earthene.com/mypage/login - click ESG_アプリ 認証 ログインボタン
22 event esg_app ダッシュボード_回答するボタン https://ecr.earthene.com/mypage/dashboard - click ESG_アプリ ダッシュボード ダッシュボード_回答するボタン
23 event esg_app ダッシュボード_詳細ボタン https://ecr.earthene.com/mypage/dashboard - click ESG_アプリ ダッシュボード ダッシュボード_詳細ボタン
24 event esg_app 調査票/サーベイ_一時保存ボタン https://ecr.earthene.com/mypage/upload/common - click ESG_アプリ 調査票/サーベイ 調査票/サーベイ_一時保存ボタン
25 event esg_app 調査票/サーベイ_提出ボタン https://ecr.earthene.com/mypage/upload/common - click ESG_アプリ 調査票/サーベイ 調査票/サーベイ_提出ボタン
26 event esg_app 調査票/サーベイ_設問ダウンロードボタン https://ecr.earthene.com/mypage/upload/common - file_download ESG_アプリ 調査票/サーベイ 調査票/サーベイ_設問ダウンロードボタン
27 event esg_app スコアカード一覧_詳細ボタン https://ecr.earthene.com/mypage/scorecard - click ESG_アプリ スコアカード スコアカード一覧_詳細ボタン
28 event esg_app スコアカード詳細_CSV出力ボタン https://ecr.earthene.com/mypage/scorecard/detail - file_download ESG_アプリ スコアカード スコアカード詳細_CSV出力ボタン
29 event esg_app スコアカード詳細_詳細ボタン https://ecr.earthene.com/mypage/scorecard/detail - click ESG_アプリ スコアカード スコアカード詳細_詳細ボタン
30 event esg_app 改善計画_作成ボタン https://ecr.earthene.com/mypage/improvementplans - click ESG_アプリ 改善計画 改善計画_作成ボタン
31 event esg_app 改善計画_詳細ボタン https://ecr.earthene.com/mypage/improvementplans - click ESG_アプリ 改善計画 改善計画_詳細ボタン
32 event esg_app ESGデータシェア_回答データ一覧_サプライヤー https://ecr.earthene.com/mypage/datashare/ - page_view ESG_アプリ ESGデータシェア ESGデータシェア_回答データ一覧_サプライヤー
33 event esg_app ESGデータシェア_回答データ詳細_サプライヤー https://ecr.earthene.com/mypage/datashare/detail - page_view ESG_アプリ ESGデータシェア ESGデータシェア_回答データ詳細_サプライヤー
34 event esg_app ESGデータシェア_回答データ編集_サプライヤー https://ecr.earthene.com/mypage/datashare/edit - page_view ESG_アプリ ESGデータシェア ESGデータシェア_回答データ編集_サプライヤー
35 event esg_app ESGデータシェア_共有データ一覧_サプライヤー https://ecr.earthene.com/mypage/datashare/company - page_view ESG_アプリ ESGデータシェア ESGデータシェア_共有データ一覧_サプライヤー
36 event esg_app ESGデータシェア_共有データ一覧_バイヤー https://ecr.earthene.com/mypage/buyer/datashare/ - page_view ESG_アプリ ESGデータシェア ESGデータシェア_共有データ一覧_バイヤー
37 event esg_app ESGデータシェア_回答データ詳細_バイヤー https://ecr.earthene.com/mypage/buyer/datashare/detail - page_view ESG_アプリ ESGデータシェア ESGデータシェア_回答データ詳細_バイヤー
38 event esg_app 改善計画_a_ASUENEバナー_表示 https://ecr.earthene.com/mypage/improvementplans - page_view ESG_アプリ 改善計画 改善計画_a_ASUENEバナー_表示
39 event esg_app 改善計画_b_CAREERバナー_表示 https://ecr.earthene.com/mypage/improvementplans - page_view ESG_アプリ 改善計画 改善計画_b_CAREERバナー_表示
40 event esg_app 改善計画_a_ASUENEバナー_クリック https://ecr.earthene.com/mypage/improvementplans - click ESG_アプリ 改善計画 改善計画_a_ASUENEバナー_クリック
41 event esg_app 改善計画_b_CAREERバナー_クリック https://ecr.earthene.com/mypage/improvementplans - click ESG_アプリ 改善計画 改善計画_b_CAREERバナー_クリック
42 event esg_app 改善計画_a_ASUENEバナー_送信 https://ecr.earthene.com/mypage/improvementplans - click ESG_アプリ 改善計画 改善計画_a_ASUENEバナー_送信クリック
43 event esg_app 改善計画_b_CAREERバナー_送信 https://ecr.earthene.com/mypage/improvementplans - click ESG_アプリ 改善計画 改善計画_b_CAREERバナー_送信クリック
44 event esg_app ログイン_アスエネCAREER_お問い合わせ https://ecr.earthene.com/mypage/login - click ESG_アプリ ログイン アスエネCAREER_a_お問い合わせ_jp
45 event esg_app ログイン_アスエネESG-DATA_お問い合わせ https://ecr.earthene.com/mypage/login - click ESG_アプリ ログイン アスエネESG-DATA_b_お問い合わせ_jp
46 event esg_app ログイン_アスエネ_無料デモ_jp https://ecr.earthene.com/mypage/login - click ESG_アプリ ログイン アスエネ_cd_無料デモ_jp
47 event esg_app ログイン_アスエネ_資料請求_jp https://ecr.earthene.com/mypage/login - click ESG_アプリ ログイン アスエネ_cd_資料ダウンロード_jp
48 event esg_app ログイン_アスエネ_無料デモ_en https://ecr.earthene.com/mypage/login - click ESG_アプリ ログイン アスエネ_ab_無料デモ_en
49 event esg_app ログイン_アスエネ_資料請求_en https://ecr.earthene.com/mypage/login - click ESG_アプリ ログイン アスエネ_ab_資料ダウンロード_en
50 event esg_app ログイン_アスエネLCA_資料請求 https://ecr.earthene.com/mypage/login - click ESG_アプリ ログイン アスエネLCA_cd_資料ダウンロード_en
51 event esg_app ログイン_アスエネLCA_お問い合わせ https://ecr.earthene.com/mypage/login - click ESG_アプリ ログイン アスエネLCA_cd_お問い合わせ_en
52 event esg_app ESGデータシェア_回答データ詳細_データ共有 https://ecr.earthene.com/mypage/datashare/detail - click ESG_アプリ ESGデータシェア ESGデータシェア_回答データ詳細_データ共有
53 event esg_app 回答閲覧認証画面  - page_view ESG_アプリ 回答閲覧 回答閲覧認証画面
54 event esg_app 回答閲覧画面 https://ecr.earthene.com/guest/upload/common - page_view ESG_アプリ 回答閲覧 回答閲覧画面
55 event esg_app ログイン_アスエネLCA_お問い合わせ https://ecr.earthene.com/mypage/login - click ESG_アプリ ログイン アスエネLCA_b_資料ダウンロード_jp
56 event esg_app ログイン_アスエネLCA_お問い合わせ https://ecr.earthene.com/mypage/login - click ESG_アプリ ログイン アスエネLCA_b_お問い合わせ_jp
57 event esg_app ログイン_アスエネLCA_お問い合わせ https://ecr.earthene.com/mypage/login - click ESG_アプリ ログイン アスエネLCA_b_jp
58 event esg_app ログイン_アスエネCAREER https://ecr.earthene.com/mypage/login - click ESG_アプリ ログイン アスエネCAREER_a_jp
59 event esg_app ログイン_アスエネ_jp https://ecr.earthene.com/mypage/login - click ESG_アプリ ログイン アスエネ_cd_jp
60 event esg_app ログイン_アスエネ_en https://ecr.earthene.com/mypage/login - click ESG_アプリ ログイン アスエネ_ab_en
61 event esg_app ログイン_アスエネLCA_en https://ecr.earthene.com/mypage/login - click ESG_アプリ ログイン アスエネLCA_cd_en
62 event esg_app 改善計画_企業詳細（バイヤー） https://ecr.earthene.com/mypage/improvementplans - page_view ESG_アプリ 改善計画 改善計画_企業詳細（バイヤー）
63 event esg_app 改善計画_詳細（バイヤー） https://ecr.earthene.com/mypage/improvementplans/detail - page_view ESG_アプリ 改善計画 改善計画_詳細（バイヤー）
64 event esg_app 改善計画_詳細（サプライヤー） https://ecr.earthene.com/mypage/improvementplans/detail - page_view ESG_アプリ 改善計画 改善計画_詳細（サプライヤー）
65 event esg_app アンケート作成_調査管理 https://ecr.earthene.com/mypage/question-groups - page_view ESG_アプリ アンケート作成 アンケート作成_調査管理
66 event esg_app アンケート作成_調査管理_基本設定(新規) https://ecr.earthene.com/mypage/question-groups/new - page_view ESG_アプリ アンケート作成 アンケート作成_調査管理_基本設定_新規
67 event esg_app アンケート作成_調査管理_基本設定(一時保存後・URL発行) https://ecr.earthene.com/mypage/question-groups/:questionGroupId  page_view ESG_アプリ アンケート作成 アンケート作成_調査管理_基本設定_詳細
68 event esg_app アンケート作成_調査管理_アンケート作成 https://ecr.earthene.com/mypage/question-groups/:questionGroupId/edit - page_view ESG_アプリ アンケート作成 アンケート作成_調査管理_アンケート作成
69 event esg_app アンケート作成_調査管理_アンケート確認 https://ecr.earthene.com/mypage/question-groups/:questionGroupId/preview - page_view ESG_アプリ アンケート作成 アンケート作成_調査管理_アンケート確認
70 event esg_app アンケート作成_回答一覧 https://ecr.earthene.com/mypage/question-groups/answers - page_view ESG_アプリ アンケート作成 アンケート作成_回答一覧
71 event esg_app アンケート作成_回答詳細 https://ecr.earthene.com/mypage/question-groups/answers/:questionGroupByCompanyId - page_view ESG_アプリ アンケート作成 アンケート作成_回答詳細
72 event esg_app 調査票/サーベイプレビュー https://ecr.earthene.com/mypage/scorecard/detail/preview - page_view ESG_アプリ 調査票/サーベイ 調査票/サーベイプレビュー
 */
// 0002
export const trackPVLogin = () => _trackEvent('custom_page_view', {
  event_label: 'ログイン',
  event_action: '認証',
})
// 0003
export const trackPVResetPassword = () => _trackEvent('custom_page_view', {
  event_label: 'パスワードリセット_メールアドレス入力',
  event_action: '認証',
})
// 0004
export const trackPVResetPasswordConfirm = () => _trackEvent('custom_page_view', {
  event_label: 'パスワードリセット_パスワード入力',
  event_action: '認証',
})
// 0005
export const trackPVResetPasswordComplete = () => _trackEvent('custom_page_view', {
  event_label: 'パスワードリセット_完了',
  event_action: '認証',
})
// 0006
export const trackPVLoginMFA = () => _trackEvent('custom_page_view', {
  event_label: 'SMS認証_認証コード入力',
  event_action: '認証',
})
// 0007
export const trackPVDashboardSupplier = () => _trackEvent('custom_page_view', {
  event_label: 'ダッシュボード_サプライヤー',
  event_action: 'ダッシュボード',
})
// 0008
export const trackPVDashboardBuyer = () => _trackEvent('custom_page_view', {
  event_label: 'ダッシュボード_バイヤー',
  event_action: 'ダッシュボード',
})
// 0009
export const trackPVSurveyForm = () => _trackEvent('custom_page_view', {
  event_label: '調査票/サーベイ',
  event_action: '調査票/サーベイ',
})
// 0010
export const trackPVScoreCardList = () => _trackEvent('custom_page_view', {
  event_label: 'スコアカード_一覧',
  event_action: 'スコアカード',
})
// 0011
export const trackPVScoreCardItem = () => _trackEvent('custom_page_view', {
  event_label: 'スコアカード_詳細',
  event_action: 'スコアカード',
})
// 0012
export const trackPVAnalysis = () => _trackEvent('custom_page_view', {
  event_label: '分析',
  event_action: '分析',
})
// 0013
export const trackPVImprovementPlanDetail = () => _trackEvent('custom_page_view', {
  event_label: '改善計画_企業詳細（サプライヤー）',
  event_action: '改善計画',
})
// 0014
export const trackPVImprovementPlanListBuyer = () => _trackEvent('custom_page_view', {
  event_label: '改善計画_企業一覧（バイヤー）',
  event_action: '改善計画',
})
// 0016
export const trackPVSettingsAccountList = () => _trackEvent('custom_page_view', {
  event_label: '設定_アカウント情報',
  event_action: '設定',
})
// 0017
export const trackPVSettingsAccountAdd = () => _trackEvent('custom_page_view', {
  event_label: '設定_ユーザー追加',
  event_action: '設定',
})
// 0018
export const trackPVSettingsAccountEdit = () => _trackEvent('custom_page_view', {
  event_label: '設定_ユーザー編集',
  event_action: '設定',
})
// 0019
export const trackClickLangSwitch = (params: { langCode: string }) => _trackEvent('click', {
  event_label: '言語切り替えボタン',
  event_action: '共通',
  lang_code: params.langCode, // スプシの要件にはないが入れておく
})
// 0020
export const trackClickLogout = () => _trackEvent('click', {
  event_label: 'ログアウトボタン',
  event_action: '認証',
})
// 0021
export const trackClickLogin = () => _trackEvent('click', {
  event_label: 'ログインボタン',
  event_action: '認証',
})
// 0022
export const trackClickDashboardAnswer = (params: ProgressSummaryWithoutEvaluated) => _trackEvent('click', {
  event_label: 'ダッシュボード_回答するボタン',
  event_action: 'ダッシュボード',
  question_group_by_companyId: params.questionGroupByCompanyId, // スプシの要件にはないが入れておく
  answer_status: params.status, // スプシの要件にはないが入れておく // MEMO: status に応じてイベント自体分けてもいいかもしれない。
})
// 0023
export const trackClickDashboardDetail = (params: { questionGroupByCompanyId: string, status: number }) => _trackEvent('click', {
  event_label: 'ダッシュボード_詳細ボタン',
  event_action: 'ダッシュボード',
  question_group_by_companyId: params.questionGroupByCompanyId, // スプシの要件にはないが入れておく
  answer_status: params.status, // スプシの要件にはないが入れておく
})
// 0024
export const trackClickSurveySave = (params: { questionGroupByCompanyId: string }) => _trackEvent('click', {
  event_label: '調査票/サーベイ_一時保存ボタン',
  event_action: '調査票/サーベイ',
  question_group_by_companyId: params.questionGroupByCompanyId, // スプシの要件にはないが入れておく
})
// 0025
export const trackClickSurveySubmit = (params: { questionGroupByCompanyId: string }) => _trackEvent('click', {
  event_label: '調査票/サーベイ_提出ボタン',
  event_action: '調査票/サーベイ',
  question_group_by_companyId: params.questionGroupByCompanyId, // スプシの要件にはないが入れておく
})
// 0026
export const trackFileDownloadSurvey = (params: QuestionGroupByCompaniesProgressSummary) => _trackEvent('file_download', {
  event_label: '調査票/サーベイ_設問ダウンロードボタン',
  event_action: '調査票/サーベイ',
  question_group_by_companyId: params.questionGroupByCompanyId, // スプシの要件にはないが入れておく
  answer_status: params.status, // スプシの要件にはないが入れておく
})
// 0027
export const trackClickScoresDetail = (params: EvaluationForBuyer | EvaluationListResponse['evaluations'][number]) => _trackEvent('click', {
  event_label: 'スコアカード一覧_詳細ボタン',
  event_action: 'スコアカード',
  evaluation_id: params?.evaluationId || null, // スプシの要件にはないが入れておく & ADataTableAndPagenation の generics がうまく効いていない状態で型が怪しいので null safe な形にしておく。
  evaluation_status: params?.status || null, // スプシの要件にはないが入れておく & ADataTableAndPagenation の generics がうまく効いていない状態で型が怪しいので null safe な形にしておく。
})
// 0028
export const trackFileDownloadScoresDetail = (params: { evaluationId: string, questionGroupByCompanyId: string }) => _trackEvent('file_download', {
  event_label: 'スコアカード詳細_CSV出力ボタン',
  event_action: 'スコアカード',
  question_group_by_companyId: params.questionGroupByCompanyId, // スプシの要件にはないが入れておく
  evaluation_id: params?.evaluationId || null, // スプシの要件にはないが入れておく
})
// 0029
export const trackClickScoresDetailDetail = (params: { evaluationId: string, indicatedPointId: string }) => _trackEvent('click', {
  event_label: 'スコアカード詳細_詳細ボタン',
  event_action: 'スコアカード',
  evaluation_id: params.evaluationId, // スプシの要件にはないが入れておく
  indicated_point_id: params.indicatedPointId, // スプシの要件にはないが入れておく
})
// 0030
export const trackClickImprovementPlansCreate = (props: { operationId: string }) => _trackEvent('click', {
  event_label: '改善計画_作成ボタン',
  event_action: '改善計画',
  operation_id: props.operationId,
})
// 0031
export const trackClickImprovementPlansDetail = (props: { operationId: string, completionStatus: CompletionStatus }) => _trackEvent('click', {
  event_label: '改善計画_詳細ボタン',
  event_action: '改善計画',
  operation_id: props.operationId,
  completion_status: props.completionStatus,
})
// 0032
export const trackPVDataShareSupplierList = () => _trackEvent('custom_page_view', {
  event_label: 'ESGデータシェア_回答データ一覧_サプライヤー',
  event_action: 'ESGデータシェア',
})
// 0033
export const trackPVDataShareSupplierDetail = () => _trackEvent('custom_page_view', {
  event_label: 'ESGデータシェア_回答データ詳細_サプライヤー',
  event_action: 'ESGデータシェア',
})
// 0034
export const trackPVDataShareSupplierEdit = () => _trackEvent('custom_page_view', {
  event_label: 'ESGデータシェア_回答データ編集_サプライヤー',
  event_action: 'ESGデータシェア',
})
// 0035
export const trackPVDataShareSupplierCompanyList = () => _trackEvent('custom_page_view', {
  event_label: 'ESGデータシェア_共有データ一覧_サプライヤー',
  event_action: 'ESGデータシェア',
})
// 0036
export const trackPVDataShareBuyerCompanyList = () => _trackEvent('custom_page_view', {
  event_label: 'ESGデータシェア_共有データ一覧_バイヤー',
  event_action: 'ESGデータシェア',
})
// 0037
export const trackPVDataShareBuyerDetail = () => _trackEvent('custom_page_view', {
  event_label: 'ESGデータシェア_回答データ詳細_バイヤー',
  event_action: 'ESGデータシェア',
})
// 0038
export const trackPVImprovementPlansAsueneBanner = () => _trackEvent('custom_page_view', {
  event_label: '改善計画_a_ASUENEバナー_表示',
  event_action: '改善計画',
})
// 0039
export const trackPVImprovementPlansCareerBanner = () => _trackEvent('custom_page_view', {
  event_label: '改善計画_b_CAREERバナー_表示',
  event_action: '改善計画',
})
// 0040
export const trackClickImprovementPlansAsueneBanner = () => _trackEvent('click', {
  event_label: '改善計画_a_ASUENEバナー_クリック',
  event_action: '改善計画',
})
// 0041
export const trackClickImprovementPlansCareerBanner = () => _trackEvent('click', {
  event_label: '改善計画_b_CAREERバナー_クリック',
  event_action: '改善計画',
})
// 0042
export const trackClickImprovementPlansAsueneBannerSubmit = () => _trackEvent('click', {
  event_label: '改善計画_a_ASUENEバナー_送信クリック',
  event_action: '改善計画',
})
// 0043
export const trackClickImprovementPlansCareerBannerSubmit = () => _trackEvent('click', {
  event_label: '改善計画_b_CAREERバナー_送信クリック',
  event_action: '改善計画',
})
// 0044
export const trackClickLoginAsueneCareer = () => _trackEvent('click', {
  event_label: 'アスエネCAREER_a_お問い合わせ_jp',
  event_action: 'ログイン',
})
// 0045
export const trackClickLoginAsueneEsgData = () => _trackEvent('click', {
  event_label: 'アスエネESG-DATA_b_お問い合わせ_jp',
  event_action: 'ログイン',
})
// 0046
export const trackClickLoginAsueneDemoJp = () => _trackEvent('click', {
  event_label: 'アスエネ_cd_無料デモ_jp',
  event_action: 'ログイン',
})
// 0047
export const trackClickLoginAsueneRequestJp = () => _trackEvent('click', {
  event_label: 'アスエネ_cd_資料ダウンロード_jp',
  event_action: 'ログイン',
})
// 0048
export const trackClickLoginAsueneDemoEn = () => _trackEvent('click', {
  event_label: 'アスエネ_ab_無料デモ_en',
  event_action: 'ログイン',
})
// 0049
export const trackClickLoginAsueneRequestEn = () => _trackEvent('click', {
  event_label: 'アスエネ_ab_資料ダウンロード_en',
  event_action: 'ログイン',
})
// 0050
export const trackClickLoginAsueneLcaRequest = () => _trackEvent('click', {
  event_label: 'アスエネLCA_cd_資料ダウンロード_en',
  event_action: 'ログイン',
})
// 0051
export const trackClickLoginAsueneLcaContact = () => _trackEvent('click', {
  event_label: 'アスエネLCA_cd_お問い合わせ_en',
  event_action: 'ログイン',
})
// 0052
export const trackClickDataShareSupplierDetailShareModal = () => _trackEvent('click', {
  event_label: 'ESGデータシェア_回答データ詳細_データ共有',
  event_action: 'ESGデータシェア',
})
// 0053
export const trackPVAnswerInvitationVerify = () => _trackEvent('custom_page_view', {
  event_label: '回答閲覧認証画面',
  event_action: '回答閲覧',
})
// 0054
export const trackPVAnswerInvitationView = () => _trackEvent('custom_page_view', {
  event_label: '回答閲覧画面',
  event_action: '回答閲覧',
})
// 0055
export const trackClickLoginAsueneLcaRequestJp = () => _trackEvent('click', {
  event_label: 'アスエネLCA_b_資料ダウンロード_jp',
  event_action: 'ログイン',
})
// 0056
export const trackClickLoginAsueneLcaContactJp = () => _trackEvent('click', {
  event_label: 'アスエネLCA_b_お問い合わせ_jp',
  event_action: 'ログイン',
})
// 0057
export const trackClickLoginAsueneLcaLpJp = () => _trackEvent('click', {
  event_label: 'アスエネLCA_b_jp',
  event_action: 'ログイン',
})
// 0058
export const trackClickLoginAsueneCareerLpJp = () => _trackEvent('click', {
  event_label: 'アスエネCAREER_a_jp',
  event_action: 'ログイン',
})
// 0059
export const trackClickLoginAsueneProductLpJp = () => _trackEvent('click', {
  event_label: 'アスエネ_cd_jp',
  event_action: 'ログイン',
})
// 0060
export const trackClickLoginAsueneProductLpEn = () => _trackEvent('click', {
  event_label: 'アスエネ_ab_en',
  event_action: 'ログイン',
})
// 0061
export const trackClickLoginAsueneLcaLpEn = () => _trackEvent('click', {
  event_label: 'アスエネLCA_cd_en',
  event_action: 'ログイン',
})
// 0062
export const trackPVImprovementPlanBuyerDetail = () => _trackEvent('custom_page_view', {
  event_label: '改善計画_企業詳細（バイヤー）',
  event_action: '改善計画',
})
// 0063
export const trackPVImprovementPlanDetailBuyer = () => _trackEvent('custom_page_view', {
  event_label: '改善計画_詳細（バイヤー）',
  event_action: '改善計画',
})
// 0064
export const trackPVImprovementPlanDetailSupplier = () => _trackEvent('custom_page_view', {
  event_label: '改善計画_詳細（サプライヤー）',
  event_action: '改善計画',
})
// 0065
export const trackPVQuestionGroupManagement = () => _trackEvent('custom_page_view', {
  event_label: 'アンケート作成_調査管理',
  event_action: 'アンケート作成',
})
// 0066
export const trackPVQuestionGroupBasicSettingsNew = () => _trackEvent('custom_page_view', {
  event_label: 'アンケート作成_調査管理_基本設定_新規',
  event_action: 'アンケート作成',
})
// 0067
export const trackPVQuestionGroupBasicSettingsDetail = () => _trackEvent('custom_page_view', {
  event_label: 'アンケート作成_調査管理_基本設定_詳細',
  event_action: 'アンケート作成',
})
// 0068
export const trackPVQuestionGroupEdit = () => _trackEvent('custom_page_view', {
  event_label: 'アンケート作成_調査管理_アンケート作成',
  event_action: 'アンケート作成',
})
// 0069
export const trackPVQuestionGroupConfirm = () => _trackEvent('custom_page_view', {
  event_label: 'アンケート作成_調査管理_アンケート確認',
  event_action: 'アンケート作成',
})
// 0070
export const trackPVQuestionGroupAnswerList = () => _trackEvent('custom_page_view', {
  event_label: 'アンケート作成_回答一覧',
  event_action: 'アンケート作成',
})
// 0071
export const trackPVQuestionGroupAnswerDetail = () => _trackEvent('custom_page_view', {
  event_label: 'アンケート作成_回答詳細',
  event_action: 'アンケート作成',
})
// 0072
export const trackPVSurveyPreview = () => _trackEvent('custom_page_view', {
  event_label: '調査票/サーベイプレビュー',
  event_action: '調査票/サーベイ',
})
// 0073
export const trackPVContact = () => _trackEvent('custom_page_view', {
  event_label: 'ログイン_お問い合わせ',
  event_action: '認証',
})
